import { useTranslation } from 'react-i18next';

const FEATURE_KEYS = ['seller', 'product', 'qc', 'shipping', 'safety', 'updated'] as const;

const FEATURE_ICONS: Record<string, string> = {
  seller: 'ri-store-2-line',
  product: 'ri-shirt-line',
  qc: 'ri-focus-3-line',
  shipping: 'ri-truck-line',
  safety: 'ri-shield-check-line',
  updated: 'ri-refresh-line',
};

const FEATURE_IMAGES: Record<string, string> = {
  seller:
    'https://readdy.ai/api/search-image?query=A%20bright%20modern%20boutique%20clothing%20store%20interior%20with%20neat%20wooden%20racks%20of%20folded%20garments%20and%20hanging%20apparel%2C%20warm%20natural%20light%20streaming%20through%20large%20windows%2C%20soft%20neutral%20tones%2C%20clean%20minimal%20retail%20space%2C%20premium%20editorial%20interior%20photography%2C%20airy%20and%20inviting%20atmosphere&width=800&height=600&seq=feature-seller&orientation=landscape',
  product:
    'https://readdy.ai/api/search-image?query=A%20tidy%20stack%20of%20neatly%20folded%20premium%20clothing%20in%20soft%20neutral%20colors%20arranged%20on%20a%20light%20wooden%20shelf%2C%20minimalist%20product%20display%2C%20soft%20diffused%20natural%20light%2C%20clean%20modern%20retail%20aesthetic%2C%20warm%20inviting%20tones%2C%20premium%20editorial%20photography%2C%20high%20detail&width=800&height=600&seq=feature-product&orientation=landscape',
  qc:
    'https://readdy.ai/api/search-image?query=A%20close%20up%20of%20careful%20hands%20inspecting%20the%20fine%20stitching%20and%20fabric%20detail%20of%20a%20premium%20garment%20with%20a%20magnifying%20glass%20nearby%20on%20a%20clean%20wooden%20table%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20premium%20editorial%20photography%2C%20detail%20oriented%20and%20trustworthy%20mood&width=800&height=600&seq=feature-qc&orientation=landscape',
  shipping:
    'https://readdy.ai/api/search-image?query=A%20neat%20kraft%20cardboard%20shipping%20box%20with%20a%20blank%20label%20and%20packing%20tape%20on%20a%20clean%20light%20wooden%20surface%20beside%20soft%20folded%20tissue%20paper%2C%20soft%20diffused%20natural%20light%2C%20warm%20neutral%20tones%2C%20clean%20minimal%20ecommerce%20shipping%20concept%2C%20premium%20editorial%20photography&width=800&height=600&seq=feature-shipping&orientation=landscape',
  safety:
    'https://readdy.ai/api/search-image?query=A%20minimal%20scene%20of%20a%20small%20brass%20padlock%20resting%20beside%20a%20smartphone%20showing%20a%20secure%20checkout%20screen%20on%20a%20clean%20light%20desk%20surface%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20trustworthy%20secure%20concept%2C%20premium%20editorial%20photography%2C%20clean%20and%20calm%20mood&width=800&height=600&seq=feature-safety&orientation=landscape',
  updated:
    'https://readdy.ai/api/search-image?query=A%20clean%20minimalist%20desk%20scene%20with%20an%20open%20notebook%20and%20a%20small%20calendar%20with%20a%20subtle%20refresh%20icon%20drawn%20in%20pen%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20organized%20and%20fresh%20concept%2C%20premium%20editorial%20photography%2C%20calm%20and%20tidy%20atmosphere&width=800&height=600&seq=feature-updated&orientation=landscape',
};

export default function Features() {
  const { t } = useTranslation();

  return (
    <>
      <section className="px-4 md:px-6 py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              {t('about.title')}
            </h2>
            <p className="mt-4 text-foreground-600 text-base leading-relaxed">{t('about.p1')}</p>
            <p className="mt-3 text-foreground-600 text-base leading-relaxed">{t('about.p2')}</p>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-white font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              {t('about.cta')}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
          <div className="w-full">
            <img
              src="https://readdy.ai/api/search-image?query=Stylish%20minimal%20flat%20lay%20of%20assorted%20premium%20fashion%20products%20including%20sneakers%20folded%20clothing%20a%20leather%20bag%20and%20accessories%2C%20arranged%20on%20a%20soft%20warm%20cream%20surface%2C%20soft%20natural%20lighting%2C%20clean%20modern%20editorial%20aesthetic%2C%20warm%20neutral%20tones%2C%20high%20detail%2C%20airy%20fresh%20composition%2C%20no%20text%20no%20watermark&width=1200&height=900&seq=hero-flatlay&orientation=landscape"
              alt={t('about.title')}
              title={t('about.title')}
              className="w-full h-56 md:h-80 object-cover object-top rounded-lg border border-background-200"
            />
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              {t('features.title')}
            </h2>
            <p className="mt-3 text-foreground-600 text-sm md:text-base">{t('features.subtitle')}</p>
          </div>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {FEATURE_KEYS.map((key) => (
              <div
                key={key}
                className="rounded-lg border border-background-200 bg-white overflow-hidden"
              >
                <div className="w-full h-36 md:h-44 overflow-hidden">
                  <img
                    src={FEATURE_IMAGES[key]}
                    alt={t(`features.${key}.title`)}
                    title={t(`features.${key}.title`)}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="w-11 h-11 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                    <i className={`${FEATURE_ICONS[key]} text-xl`} />
                  </div>
                  <h3 className="mt-4 font-heading font-semibold text-foreground-950 text-base md:text-lg">
                    {t(`features.${key}.title`)}
                  </h3>
                  <p className="mt-2 text-foreground-600 text-sm leading-relaxed">
                    {t(`features.${key}.desc`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}